/**
 * Recruiter "calendars" picker for My Calendar (admins only).
 *
 * Each teammate is a calendar you can overlay. Pick "All recruiters" to see
 * everyone's callbacks & tasks, or check any combination of people. The dropdown
 * keeps your own calendar first ("(you)") and color-codes each person with the
 * same hue used on the calendar chips/dots. At least one calendar stays
 * selected — unchecking the last one falls back to you.
 *
 * Value shape: { all: true } shows everyone; otherwise { all: false, ids: [...] }.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Users } from 'lucide-react';
import { TeamMember } from '../../types';
import { personColor } from './colors';

export interface AssigneeSelection {
  all: boolean;
  ids: string[];
}

interface Props {
  team: TeamMember[];
  currentUserId: string | undefined;
  value: AssigneeSelection;
  onChange: (v: AssigneeSelection) => void;
}

export default function RecruiterMultiSelect({ team, currentUserId, value, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // Your own calendar first, then the rest by name.
  const ordered = useMemo(() => {
    const me = team.filter((m) => m.id === currentUserId);
    const others = team
      .filter((m) => m.id !== currentUserId)
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    return [...me, ...others];
  }, [team, currentUserId]);

  const selected = new Set(value.ids);

  const toggleAll = () => {
    if (value.all) onChange({ all: false, ids: currentUserId ? [currentUserId] : [] });
    else onChange({ all: true, ids: [] });
  };

  const togglePerson = (id: string) => {
    if (value.all) {
      // Leaving "all" mode by deselecting one person: keep everyone else.
      onChange({ all: false, ids: ordered.map((m) => m.id).filter((x) => x !== id) });
      return;
    }
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    const ids = [...next];
    onChange({ all: false, ids: ids.length ? ids : currentUserId ? [currentUserId] : [] });
  };

  const summary = value.all
    ? 'All recruiters'
    : value.ids.length <= 1
    ? value.ids[0] === currentUserId || value.ids.length === 0
      ? 'My calendar'
      : team.find((m) => m.id === value.ids[0])?.name || '1 recruiter'
    : `${value.ids.length} recruiters`;

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
        title="Choose whose calendars to show"
      >
        <Users className="h-4 w-4 text-gray-400" />
        <span className="max-w-[10rem] truncate">{summary}</span>
        <ChevronDown className="h-4 w-4 text-gray-400" />
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-1 max-h-80 w-60 max-w-[calc(100vw-2rem)] overflow-y-auto rounded-xl border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800">
          <button
            type="button"
            onClick={toggleAll}
            className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700/50"
          >
            <span
              className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border ${
                value.all ? 'border-primary-600 bg-primary-600 text-white' : 'border-gray-300 dark:border-gray-500'
              }`}
            >
              {value.all && <Check className="h-3 w-3" strokeWidth={3} />}
            </span>
            <span className="font-medium text-gray-900 dark:text-gray-100">All recruiters</span>
          </button>

          <div className="my-1 border-t border-gray-100 dark:border-gray-700" />

          {ordered.map((m) => {
            const checked = value.all || selected.has(m.id);
            const isMe = m.id === currentUserId;
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => togglePerson(m.id)}
                className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700/50"
              >
                <span
                  className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border ${
                    checked ? 'border-primary-600 bg-primary-600 text-white' : 'border-gray-300 dark:border-gray-500'
                  }`}
                >
                  {checked && <Check className="h-3 w-3" strokeWidth={3} />}
                </span>
                <span className={`h-2.5 w-2.5 flex-shrink-0 rounded-full ${personColor(m.id).dot}`} />
                <span className="min-w-0 flex-1 truncate text-gray-800 dark:text-gray-200">
                  {m.name}
                  {isMe && <span className="ml-1 text-xs text-gray-400">(you)</span>}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
